import type { Message } from "ls/api/cloud/ai";
import type { RefOrGetter } from "ls/common/types";
import { getMarkdown, useAiChat } from "ls/api/cloud/ai";
import MarkdownIt from "markdown-it";
import { computed, ref, toValue, watch } from "vue";

const tagName = "DocumentPrompt";
const openTag = `<${tagName}>`;
const closeTag = `</${tagName}>`;

export function getMessageHtml(message: Message) {
  const content = message.content;
  if (!content) return;

  const startTagIndex = content.findIndex(c => c.text.includes(openTag));
  const endTagIndex = content.findIndex(c => c.text.includes(closeTag));
  if (startTagIndex === -1 || endTagIndex === -1) return;

  const withinTag = content.slice(startTagIndex + 1, endTagIndex);

  const markdownText = getMarkdown(withinTag);
  const md = MarkdownIt();
  const html = md.render(markdownText);

  return html;
}

export function useChatActionMessagesCollection(chatId: RefOrGetter<string>) {
  const conversation = useAiChat(toValue(chatId));

  const actionMessages = computed(() => conversation.messages.filter(m => m.content.some(c => c.text.includes(openTag))));

  const currentActionMessageId = ref<string>();

  const currentActionMessage = computed(() => actionMessages.value.find(m => m.id === currentActionMessageId.value));
  const currentActionMessageIndex = computed(() => {
    if (!currentActionMessage.value) {
      return -1;
    }
    return actionMessages.value.indexOf(currentActionMessage.value);
  });
  const currentActionMessageNumber = computed(() => currentActionMessageIndex.value + 1);

  const total = computed(() => actionMessages.value.length);
  const hasActionMessages = computed(() => total.value > 0);

  const lastActionMessage = computed(() => actionMessages.value.at(-1));
  watch(total, totalValue => {
    if (totalValue > 0) {
      const lastMessage = lastActionMessage.value;
      if (lastMessage?.id) {
        currentActionMessageId.value = lastMessage.id;
      }
    }
  });

  const currentActionMessageContent = computed(() => {
    const message = currentActionMessage.value;
    if (!message) {
      return undefined;
    }

    const content = message.content;
    const startTagIndex = content.findIndex(c => c.text.includes(openTag));

    // return all content after the tag is found and before the end tag is found where the start is <tag> and the end is </tag>
    const endTagIndex = content.findIndex(c => c.text.includes(closeTag));

    if (startTagIndex !== -1 && endTagIndex !== -1) {
      return content.slice(startTagIndex + 1, endTagIndex);
    }

    return [];
  });

  const hasPrevious = computed(() => currentActionMessageIndex.value > 0);
  const hasNext = computed(() => currentActionMessageIndex.value < actionMessages.value.length - 1);

  function selectPrevious() {
    if (!hasPrevious.value) {
      return;
    }
    const previous = actionMessages.value[currentActionMessageIndex.value - 1];
    if (previous) {
      currentActionMessageId.value = previous.id;
    }
  }

  function selectNext() {
    if (!hasNext.value) {
      return;
    }
    const next = actionMessages.value[currentActionMessageIndex.value + 1];
    currentActionMessageId.value = next.id;
  }

  function selectMessage(messageId: string) {
    currentActionMessageId.value = messageId;
  }

  const isLoading = computed(() => {
    return !currentActionMessage.value || (currentActionMessage.value?.content.length === 0 && !currentActionMessage.value?.incomingMessageContent);
  });

  const isIncoming = computed(() => !currentActionMessage.value || currentActionMessage.value.isIncoming);

  return {
    actionMessages,
    total,
    hasActionMessages,
    hasPrevious,
    hasNext,
    lastActionMessage,
    currentActionMessage,
    currentActionMessageNumber,
    currentActionMessageContent,
    isLoading,
    isIncoming,
    selectPrevious,
    selectNext,
    selectMessage,
  };
}
